import APIService from "./APIService";

/**
 * Browser Fingerprint API Service
 * Handles browser fingerprint collection and submission for security tracking
 * Implements non-blocking parallel execution with user authentication
 */
const BrowserFingerprintAPI = {
  /**
   * Collect browser fingerprint with timeout and error handling
   * This runs in parallel with authentication and should not block login
   * @param {number} timeoutMs - Timeout in milliseconds (default: 5000)
   * @returns {Promise} - Promise that resolves with fingerprint data or null on failure
   */
  collectFingerprintData: async (timeoutMs = 5000) => {
    return new Promise(async (resolve) => {
      const timeoutId = setTimeout(() => {
        console.warn("BrowserFingerprintAPI: Fingerprint collection timed out");
        resolve(null); // Resolve with null instead of rejecting
      }, timeoutMs);

      try {
        console.log("BrowserFingerprintAPI: Starting fingerprint collection");

        // Import FingerprintJS dynamically
        const FingerprintJS = await import("@fingerprintjs/fingerprintjs");

        // Initialize FingerprintJS agent with timeout
        const fp = await FingerprintJS.load();

        // Get the fingerprint
        const result = await fp.get();

        // Collect additional browser information
        const additionalData = {
          userAgent: navigator.userAgent,
          language: navigator.language,
          languages: navigator.languages ? Array.from(navigator.languages) : [],
          platform: navigator.platform,
          cookieEnabled: navigator.cookieEnabled,
          doNotTrack: navigator.doNotTrack,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timezoneOffset: new Date().getTimezoneOffset(),
          // eslint-disable-next-line no-restricted-globals
          screen: {
            width: window.screen.width,
            height: window.screen.height,
            availWidth: window.screen.availWidth,
            availHeight: window.screen.availHeight,
            colorDepth: window.screen.colorDepth,
            pixelDepth: window.screen.pixelDepth,
          },
          window: {
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
            outerWidth: window.outerWidth,
            outerHeight: window.outerHeight,
          },
          capabilities: {
            localStorage: !!window.localStorage,
            sessionStorage: !!window.sessionStorage,
            indexedDB: !!window.indexedDB,
            webGL: !!window.WebGLRenderingContext,
            webGL2: !!window.WebGL2RenderingContext,
            webRTC: !!(
              navigator.getUserMedia ||
              navigator.webkitGetUserMedia ||
              navigator.mozGetUserMedia
            ),
          },
          performance: {
            deviceMemory: navigator.deviceMemory || null,
            hardwareConcurrency: navigator.hardwareConcurrency || null,
            maxTouchPoints: navigator.maxTouchPoints || 0,
          },
        };

        // Create fingerprint data object
        const fingerprintData = {
          fingerprintId: result.visitorId,
          confidence: result.confidence,
          fingerprintComponents: result.components,
          additionalData,
          timestamp: new Date().toISOString(),
          collectionDuration: Date.now(), // Will be calculated later
        };

        clearTimeout(timeoutId);
        console.log(
          "BrowserFingerprintAPI: Fingerprint collection completed successfully"
        );
        resolve(fingerprintData);
      } catch (error) {
        clearTimeout(timeoutId);
        console.warn(
          "BrowserFingerprintAPI: Error collecting fingerprint:",
          error
        );
        resolve(null); // Graceful degradation - resolve with null instead of rejecting
      }
    });
  },

  /**
   * Submit browser fingerprint data associated with a session ID
   * @param {Object} fingerprintData - Fingerprint data object
   * @param {string} sessionId - Session ID to associate with the fingerprint
   * @param {string} userUuid - User UUID for additional validation
   * @returns {Promise} - Promise that resolves with the API response
   */
  submitFingerprintWithSession: async (
    fingerprintData,
    sessionId,
    userUuid
  ) => {
    try {
      if (!fingerprintData) {
        console.warn(
          "BrowserFingerprintAPI: No fingerprint data to submit (collection failed)"
        );
        return { success: true, message: "Fingerprint collection was skipped" };
      }

      console.log(
        "BrowserFingerprintAPI: Submitting fingerprint data with session:",
        {
          sessionId: sessionId?.substring(0, 8) + "...", // Log partial session ID for privacy
          userUuid,
          fingerprintId: fingerprintData.fingerprintId,
          confidence: fingerprintData.confidence,
          componentsCount: Object.keys(
            fingerprintData.fingerprintComponents || {}
          ).length,
        }
      );

      const payload = {
        ...fingerprintData,
        sessionId,
        userUuid,
      };

      const response = await APIService.post(
        "/api/v1/base/browser-fingerprints/session",
        payload
      );

      console.log(
        "BrowserFingerprintAPI: Fingerprint submitted successfully with session"
      );
      return response;
    } catch (error) {
      console.error(
        "BrowserFingerprintAPI: Error submitting fingerprint with session:",
        error
      );
      // Don't throw error - this should not break the user experience
      return { success: false, error: error.message };
    }
  },

  /**
   * Orchestrates parallel authentication and fingerprint collection
   * @param {Function} authFunction - Function that performs authentication
   * @param {Object} authData - Authentication data (credentials)
   * @returns {Promise} - Promise that resolves with authentication result
   */
  parallelAuthAndFingerprint: async (authFunction, authData) => {
    const startTime = Date.now();

    try {
      console.log(
        "BrowserFingerprintAPI: Starting parallel authentication and fingerprint collection"
      );

      // Start both operations in parallel using Promise.allSettled
      const [authResult, fingerprintResult] = await Promise.allSettled([
        authFunction(authData),
        BrowserFingerprintAPI.collectFingerprintData(5000), // 5 second timeout
      ]);

      const authDuration = Date.now() - startTime;
      console.log(
        `BrowserFingerprintAPI: Authentication completed in ${authDuration}ms`
      );

      // Handle authentication result
      if (authResult.status === "rejected") {
        console.error(
          "BrowserFingerprintAPI: Authentication failed:",
          authResult.reason
        );
        throw authResult.reason;
      }

      const authResponse = authResult.value;
      console.log("BrowserFingerprintAPI: Authentication successful");

      // Handle fingerprint result (non-blocking)
      let fingerprintData = null;
      if (fingerprintResult.status === "fulfilled") {
        fingerprintData = fingerprintResult.value;
        if (fingerprintData) {
          fingerprintData.collectionDuration = Date.now() - startTime;
          console.log(
            "BrowserFingerprintAPI: Fingerprint collection successful"
          );
        } else {
          console.warn(
            "BrowserFingerprintAPI: Fingerprint collection returned null"
          );
        }
      } else {
        console.warn(
          "BrowserFingerprintAPI: Fingerprint collection failed:",
          fingerprintResult.reason
        );
      }

      // Submit fingerprint asynchronously if we have session data
      if (fingerprintData && authResponse.sessionId && authResponse.uuid) {
        // Fire and forget - don't await this
        BrowserFingerprintAPI.submitFingerprintWithSession(
          fingerprintData,
          authResponse.sessionId,
          authResponse.uuid
        ).catch((error) => {
          console.warn(
            "BrowserFingerprintAPI: Background fingerprint submission failed:",
            error
          );
        });
      }

      return authResponse;
    } catch (error) {
      console.error(
        "BrowserFingerprintAPI: Error in parallel execution:",
        error
      );
      throw error;
    }
  },
};

export default BrowserFingerprintAPI;
